import { useContext, useEffect, useState } from 'react';

import { DefaultTheme, UIThemeProvider } from '@selina-finance/ui';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AuthContext } from '@App/contexts/Auth';
import { intercomUpdate } from '@App/intercom/intercom';
import BookCallCalendarPage from '@Pages/loan-preferences/BookCallCalendarPage';
import { LoanPreferencesBookCallPage } from '@Pages/loan-preferences/LoanPreferencesBookCallPage';
import { LoanPreferencesBookingConfirmationPage } from '@Pages/loan-preferences/LoanPreferencesBookingConfirmationPage';
import { LoanPreferencesHomePage } from '@Pages/loan-preferences/LoanPreferencesHomePage';
import { LoanPreferencesQuestionsPage } from '@Pages/loan-preferences/LoanPreferencesQuestionsPage';
import { useTrackLastFactFindField } from '@src/app/hooks/trackMixpanelEventsHooks/useTrackLastFactFindField';
import { Protected, ProtectedEligibility } from '@src/ui/components/Protected';
import CreateUser from '@src/ui/pages/CreateUser';
import Login from '@src/ui/pages/Login';
import Logout from '@src/ui/pages/Logout';

import AlmostComplete from './pages/AlmostComplete';
import Applicants from './pages/Applicants';
import Dashboard from './pages/Dashbord';
import Incomings from './pages/Incomings';
import LoanDetails from './pages/LoanDetails';
import Outgoings from './pages/Outgoings';
import Reject from './pages/Reject';
import Wait from './pages/Wait';
import YourProperty from './pages/YourProperty';
import DebtInput from './pages/debtCon/DebtInput';
import Intro from './pages/debtCon/Intro';
import MainGoal from './pages/debtCon/MainGoal';
import NewDebtInput from './pages/debtCon/NewDebtInput';
import Review from './pages/debtCon/Review';
import Transition from './pages/debtCon/Transition';
import YourBorrowing from './pages/debtCon/YourBorrowing';
import Approval from './pages/offer/Approval';
import BookCall from './pages/offer/BookCall';
import Confirmation from './pages/offer/Confirmation';
import Explore from './pages/offer/Explore';
import Finalise from './pages/offer/Finalise';

const Routing = () => {
    const location = useLocation();

    const { loggedIn } = useContext(AuthContext);

    const [lastPathname, setLastPathname] = useState(location.pathname);

    useTrackLastFactFindField(location.pathname, loggedIn);

    useEffect(() => {
        if (lastPathname !== location.pathname) {
            intercomUpdate();

            setLastPathname(location.pathname);
        }
    }, [lastPathname, location.pathname]);

    return (
        <UIThemeProvider theme={DefaultTheme}>
            <Routes>
                <Route
                    index
                    element={
                        <Protected>
                            <Dashboard />
                        </Protected>
                    }
                />
                <Route path='eligibility'>
                    <Route
                        path=''
                        element={
                            <Protected>
                                <Dashboard />
                            </Protected>
                        }
                    />
                    <Route
                        path='loan-details'
                        element={
                            <Protected>
                                <ProtectedEligibility>
                                    <LoanDetails />
                                </ProtectedEligibility>
                            </Protected>
                        }
                    />
                    <Route
                        path='applicants'
                        element={
                            <Protected>
                                <ProtectedEligibility>
                                    <Applicants />
                                </ProtectedEligibility>
                            </Protected>
                        }
                    />
                    <Route
                        path='applicants2'
                        element={
                            <Protected>
                                <ProtectedEligibility>
                                    <Applicants />
                                </ProtectedEligibility>
                            </Protected>
                        }
                    />
                    <Route
                        path='property-details'
                        element={
                            <Protected>
                                <ProtectedEligibility>
                                    <YourProperty />
                                </ProtectedEligibility>
                            </Protected>
                        }
                    />
                    <Route
                        path='income'
                        element={
                            <Protected>
                                <ProtectedEligibility>
                                    <Incomings />
                                </ProtectedEligibility>
                            </Protected>
                        }
                    />
                    <Route
                        path='income2'
                        element={
                            <Protected>
                                <ProtectedEligibility>
                                    <Incomings />
                                </ProtectedEligibility>
                            </Protected>
                        }
                    />
                    <Route
                        path='outgoings'
                        element={
                            <Protected>
                                <ProtectedEligibility>
                                    <Outgoings />
                                </ProtectedEligibility>
                            </Protected>
                        }
                    />
                    <Route path='loan-preferences'>
                        <Route
                            index
                            element={<Navigate to='homepage' replace />}
                        />
                        <Route
                            path='homepage'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <LoanPreferencesHomePage />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='questions'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <LoanPreferencesQuestionsPage />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='book-call'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <LoanPreferencesBookCallPage />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='book-call/confirmation'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <LoanPreferencesBookingConfirmationPage />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='book-call/calendar'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <BookCallCalendarPage />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                    </Route>

                    <Route
                        path='create-account'
                        element={
                            <Protected>
                                <CreateUser />
                            </Protected>
                        }
                    />
                    <Route
                        path='wait'
                        element={
                            <Protected>
                                <ProtectedEligibility>
                                    <Wait />
                                </ProtectedEligibility>
                            </Protected>
                        }
                    />
                    <Route path='offer'>
                        <Route
                            path='approval'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <Approval />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='explore'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <Explore />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='finalise'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <Finalise />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='book-call'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <BookCall />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='confirmation'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <Confirmation />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                    </Route>
                    <Route path='debt-con'>
                        <Route
                            path='intro'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <Intro />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='main-goal'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <MainGoal />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='borrow'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <YourBorrowing />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='transition'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <Transition />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='input'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <DebtInput />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='new-input'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <NewDebtInput />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                        <Route
                            path='review'
                            element={
                                <Protected>
                                    <ProtectedEligibility>
                                        <Review />
                                    </ProtectedEligibility>
                                </Protected>
                            }
                        />
                    </Route>
                    <Route
                        path='almost-complete'
                        element={
                            <Protected>
                                <ProtectedEligibility>
                                    <AlmostComplete />
                                </ProtectedEligibility>
                            </Protected>
                        }
                    />
                    <Route
                        path='reject'
                        element={
                            <Protected>
                                <Reject />
                            </Protected>
                        }
                    />
                </Route>
                <Route path='login' element={<Login />} />
                <Route path='logout' element={<Logout />} />
                <Route path='create-user' element={<CreateUser />} />
                <Route path='create-password' element={<CreateUser />} />
            </Routes>
        </UIThemeProvider>
    );
};

export default Routing;
